import React, {FC, useState, useReducer, useEffect} from "react"

import {RouteComponentProps, Redirect} from "react-router"
import {Col, Row} from "react-bootstrap"
import {BulkRewardStatus, PaymentCurrencyType, WalletEntityType} from "@irati/types"
import walletService from "@irati/wallet/graph/wallet"
import authentication from "@irati/auth"
import notify from "@irati/utils/notify"
import {InjectIntl, injectIntl} from "@irati/utils/lang"
import flexiPointService from "@irati/wallet/services/flexiRewards"
import RecognitionService from "@irati/recognition/services/Recognition"
import History from "@irati/utils/history"
import * as PropTypes from "prop-types"
import {PartialRoute} from "@irati/utils/history/routing"
import Card from "@irati/ui/src/Layout/Card"
import tracking from "@irati-tracking/wallet/rewardPointsWallet"
import {parseRecognition} from "@irati/recognition/apollo/mutation/Recognition/Create"
import {EMPTY_RECOGNITION} from "@irati-web/recognition/src/components/Popup/CreateRecognition/Popup"
import {waitPromise} from "@irati/utils/promise"
import YouNeedToMfa from "@irati-web/mfa/components/Error/YouNeedToMfa"
import Loading from "@irati/ui/src/Loading"
import mfaService from "@irati/auth/services/mfa"
import FormatMessage from "@irati/utils/lang/types/FormatMessage"
import Container from "../styled/Container"
import DistributeRewardSummary from "../../components/AdminReward/DistributeReward/DistributeRewardSummary"
import {StepNames} from "../../enums"
import DistributeRewardStep1 from "../../components/AdminReward/DistributeReward/DistributeRewardStep1"
import DistributeRewardStep2 from "../../components/AdminReward/DistributeReward/DistributeRewardStep2"
import DistributeRewardStep3 from "../../components/AdminReward/DistributeReward/DistributeRewardStep3"
import {DistributeRewardImportType, DistributeRewardRecipient} from "../../enums/DistributeRewardTypes"
import DistributeRewardReducer from "../../stores/DistributeReward/reducer"
import DistributeRewardSuccessModal from "../../components/AdminReward/DistributeReward/DistributeRewardSuccessModal"
import GenericErrorModal from "../../components/AdminReward/DistributeReward/GenericErrorModal"
import {DistributeRewardActions, DistributeRewardState} from "../../stores/DistributeReward/actions"

const INITIAL_STATE = (recognitionProp: FormatMessage): DistributeRewardState => ({
  pointsPerUser: 0,
  message: "",
  recipientType: null,
  rewardImportType: DistributeRewardImportType.MANUAL_ENTER,
  destinationUsers: [],
  userEmails: [],
  recognition: EMPTY_RECOGNITION(recognitionProp),
  bulkRewardState: {
    failedEmails: [],
    uuid: null,
    csvError: null
  },
  modalErrorState: {
    title: "",
    content: "",
    buttonText: "",
    show: false
  }
})

interface Props extends InjectIntl, RouteComponentProps {}

const DistributeReward: FC<Props> = ({formatMessage, match}, context) => {
  const [mfaLoad, setMfaLoad] = useState<Promise<any>>(mfaService.hasFlag())
  const [state, dispatch] = useReducer(DistributeRewardReducer, INITIAL_STATE(formatMessage))
  const [isShownRewardConfirmation, setShowRewardConfirmation] = useState(false)
  const [allocationJourneyError, setAllocationJourneyError] = useState(false)

  const isAdmin = authentication.apps("wallet.flexiRewards.canTopUpRewardPoints")

  useEffect(() => {
    //  Check made for manager flow where recipientType can't be null but user can't select either
    if(!state.recipientType && !isAdmin) {
      dispatch({type: DistributeRewardActions.SWITCH_RECIPIENT, payload: {recipientType: DistributeRewardRecipient.EMPLOYEE}})
    }
  }, [])

  const showNotification = (text: string) => {
    const usersList = state.rewardImportType === DistributeRewardImportType.IMPORT_CSV ? state.userEmails : state.destinationUsers
    notify.success({
      message: formatMessage(
        {id: text},
        {
          count: Math.round(state.pointsPerUser) * usersList.length
        }
      )
    })
  }

  const executeBulkReward = async() => {
    if (!state.bulkRewardState.uuid) return
    try {
      const {status} = await flexiPointService.confirmBulkReward({uuid: state.bulkRewardState.uuid, message: state.message})
      if (status === BulkRewardStatus.Error || status === BulkRewardStatus.Unrecognized) {
        throw new Error(status)
      }
      setShowRewardConfirmation(true)
    } catch (e) {
      dispatch({
        type: DistributeRewardActions.SET_MODAL_ERROR_STATE,
        payload: {
          show: true,
          title: "wallet.adminReward.bulkRewards.errorModal.payment.title",
          content: "wallet.adminReward.bulkRewards.errorModal.payment.content",
          buttonText: "wallet.adminReward.bulkRewards.errorModal.payment.cta"
        }
      })
    }
  }

  const executeManualReward = async() => {
    return walletService.get(authentication.user().uuid!, PaymentCurrencyType.Rp, WalletEntityType.Reward).then((wallet) => {
      const variables = {
        sourceId: wallet.uuid!,
        destinationUserIds: state.destinationUsers.map(({uuid}) => uuid!),
        amount: state.pointsPerUser * 100,
        message: state.message
      }

      if (state.recipientType === DistributeRewardRecipient.MANAGER) {
        return flexiPointService
          .assignCredit(variables)
          .then(() => {
            History.push("/flexi-reward", context)
            showNotification("wallet.reward.admin.notification.assignedPoints")
            tracking.assignBudgetSuccess()
          })
          .catch((error) => {
            tracking.assignBudgetError(error)
          })
      }
      return flexiPointService
        .rewardEmployees(variables)
        .then(({transferIds}) => {
          if (authentication.apps("recognition")) {
            flexiPointService.getTransferOutcome(transferIds[0]).then(({status}) => {
              const poll = (): any => {
                return waitPromise(500).then(() => {
                  while (status === "PENDING") {
                    return poll()
                  }
                  if (status === "COMPLETE") {
                    return RecognitionService.create(
                      parseRecognition({
                        walletTransferId: transferIds[0],
                        ...state.recognition
                      })
                    )
                  } else {
                    throw Error("Transfer failed")
                  }
                })
              }
              return poll()
            })
          }

          History.push("/flexi-reward", context)
          showNotification("wallet.reward.admin.notification.sendReward")
          tracking.rewardEmployeesSuccess()
        })
        .catch((error) => {
          tracking.rewardEmployeesError(error)
        })
    })
  }

  const handleSubmit = async() => {
    if (state.rewardImportType === DistributeRewardImportType.IMPORT_CSV) {
      await executeBulkReward()
      return
    }
    await executeManualReward()
  }

  const handleMFARetry = async() => {
    setMfaLoad(mfaService.hasFlag())
  }

  const onCSVModalClose = () => History.push("/flexi-reward", context)

  return (
    <Loading load={mfaLoad} Error={() => <YouNeedToMfa onRetry={handleMFARetry} />}>
      <Container>
        <Row>
          <Col md={8}>
            <Card marginBottom={2}>
              <PartialRoute
                exact
                match={match.path}
                path="/"
                component={() => <Redirect to={`/wallet/flexi-assign/${StepNames.Recipients}`} />} />
              <PartialRoute
                match={match.path}
                path={`/${StepNames.Recipients}`}
                component={DistributeRewardStep1}
                componentProps={{
                  payload: state,
                  dispatch,
                  allocationJourneyError,
                  setAllocationJourneyError
                }} />
              <PartialRoute
                match={match.path}
                path={`/${StepNames.Message}`}
                component={DistributeRewardStep2}
                componentProps={{payload: state, dispatch}} />
              <PartialRoute
                match={match.path}
                path={`/${StepNames.Confirm}`}
                component={DistributeRewardStep3}
                componentProps={{payload: state}} />
            </Card>
          </Col>
          <Col md={4}>
            <DistributeRewardSummary onSubmit={handleSubmit} dispatch={dispatch} payload={state} setAllocationJourneyError={setAllocationJourneyError} />
          </Col>
        </Row>
        {isShownRewardConfirmation && <DistributeRewardSuccessModal onClose={onCSVModalClose} payload={state} />}
        {state.modalErrorState.show && <GenericErrorModal data={state.modalErrorState} />}
      </Container>
    </Loading>
  )
}

DistributeReward.contextTypes = {
  appPath: PropTypes.string.isRequired
}

export default injectIntl(DistributeReward)
